import { BOT_KNOWLEDGE } from "./botKnowledge";
import { getApiBaseUrl } from "./apiConfig";

/**
 * Lead capture conversation flow state machine.
 */
export const INITIAL_LEAD_STATE = {
  active: false,
  step: 0, // 0: ask name, 1: ask email, 2: ask service, 3: ask budget
  data: {
    name: "",
    email: "",
    projectFocus: "",
    budget: "",
    message: ""
  }
};

/**
 * Normalize and tokenize raw input text.
 */
function cleanText(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s$]/gi, " ")
    .trim();
}

/**
 * Handle incoming lead capture steps.
 */
export async function handleLeadStep(userText, leadState, setLeadState, customApiBase) {
  const currentStep = leadState.step;
  const input = userText.trim();

  // User wants to cancel lead collection
  if (["cancel", "stop", "nevermind", "exit", "no"].includes(input.toLowerCase())) {
    setLeadState(INITIAL_LEAD_STATE);
    return {
      text: "No problem! Lead setup cancelled. How else can I assist you with Metatrybe's services or pricing?",
      buttons: [
        { label: "Pricing & Plans", intent: "pricing" },
        { label: "Services Offered", intent: "services" },
        { label: "View Portfolio", intent: "portfolio" }
      ]
    };
  }

  if (currentStep === 0) {
    // Collect Name
    if (input.length < 2) {
      return {
        text: "Please enter your full name so our team knows who to address!"
      };
    }
    setLeadState({
      ...leadState,
      step: 1,
      data: { ...leadState.data, name: input }
    });
    return {
      text: `Nice to meet you, **${input}**! What is your best email address so our team can send over project details and a custom proposal?`
    };
  }

  if (currentStep === 1) {
    // Collect Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(input)) {
      return {
        text: "That email looks invalid. Please provide a valid email address (e.g., alex@company.com):"
      };
    }
    setLeadState({
      ...leadState,
      step: 2,
      data: { ...leadState.data, email: input }
    });
    return {
      text: `Got it! What service or project type are you interested in?`,
      buttons: [
        { label: "Website Design & Dev", actionValue: "Website Design & Dev" },
        { label: "UX/UI Design", actionValue: "UX/UI Design" },
        { label: "No-Code Development", actionValue: "No-Code Development" },
        { label: "Mobile / Web App Dev", actionValue: "App Development" },
        { label: "E-Commerce Store", actionValue: "E-Commerce Store" },
        { label: "Custom Solution", actionValue: "Custom Solution" }
      ]
    };
  }

  if (currentStep === 2) {
    // Collect Service
    setLeadState({
      ...leadState,
      step: 3,
      data: { ...leadState.data, projectFocus: input }
    });
    return {
      text: `Great choice! What is your estimated budget for this project?`,
      buttons: [
        { label: "$2,000 - $3,500", actionValue: "$2,000 - $3,500" },
        { label: "$3,500 - $5,000", actionValue: "$3,500 - $5,000" },
        { label: "$5,000 - $10,000+", actionValue: "$5,000 - $10,000+" },
        { label: "Recommend a budget", actionValue: "Recommend a budget" }
      ]
    };
  }

  if (currentStep === 3) {
    // Final step: Collect Budget & Submit Lead to API
    const finalData = {
      ...leadState.data,
      budget: input,
      message: `Inquiry via Metatrybe AI Chatbot for ${leadState.data.projectFocus}`
    };

    setLeadState(INITIAL_LEAD_STATE);

    const API_BASE = customApiBase !== undefined ? customApiBase : getApiBaseUrl();

    try {
      const response = await fetch(`${API_BASE}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(finalData)
      });

      if (response.ok) {
        return {
          text: `**Thank you, ${finalData.name}!** Your project inquiry for **${finalData.projectFocus}** has been sent directly to our lead team.\n\nWe have sent a confirmation email to **${finalData.email}**. Our team will review your request and reach out within **24 hours**!`,
          buttons: [
            { label: "View Pricing Plans", intent: "pricing" },
            { label: "View All Services", intent: "services" },
            { label: "View Portfolio", intent: "portfolio" }
          ]
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to submit lead");
      }
    } catch (err) {
      console.error("Lead submission API error:", err);
      return {
        text: `**Thank you, ${finalData.name}!** We've recorded your inquiry for **${finalData.projectFocus}**. If you don't receive an email within 24 hours, feel free to contact us at **hello@metatrybe.com**!`,
        buttons: [
          { label: "View Pricing Plans", intent: "pricing" },
          { label: "View All Services", intent: "services" }
        ]
      };
    }
  }

  return { text: "Something went wrong. Let's restart!" };
}

/**
 * Primary intent matching engine.
 */
export function processUserQuery(rawInput, leadState, setLeadState) {
  const text = cleanText(rawInput);

  // Intent matchers
  const isGreeting = /\b(hi|hello|hey|greetings|hola|good morning|good afternoon)\b/i.test(text);
  const isPricing = /\b(price|pricing|cost|rate|fee|charge|how much|dollar|plan|package|tier)\b/i.test(text);
  const isServices = /\b(service|services|offer|what do you do|build|develop|design|tech|technology|stack|mobile|app|website|ecommerce)\b/i.test(text);
  const isPortfolio = /\b(portfolio|work|project|projects|sample|case study|case studies|examples|past work)\b/i.test(text);
  const isContact = /\b(contact|reach|call|email|phone|address|touch|speak|talk|human|support)\b/i.test(text);
  const isLeadRequest = /\b(quote|book|hire|start|consultation|estimate|proposal|order|get started)\b/i.test(text);

  // 1. Direct Lead Flow Trigger
  if (isLeadRequest || text === "lead_start") {
    setLeadState({
      active: true,
      step: 0,
      data: { name: "", email: "", projectFocus: "", budget: "", message: "" }
    });
    return {
      text: "Awesome! Let's get your project started.\n\nFirst off, **what is your name?**",
      buttons: []
    };
  }

  // 2. Pricing Query Matcher
  if (isPricing || text === "pricing") {
    const plansText = BOT_KNOWLEDGE.pricing
      .map(
        (plan) =>
          `• **${plan.title}**: Starts at **${plan.price}** — *${plan.description}*`
      )
      .join("\n\n");

    return {
      text: `Here are **Metatrybe's pricing tiers**:\n\n${plansText}\n\n*All plans include unlimited revisions until you are 100% satisfied + 30 days of post-launch support!*`,
      buttons: [
        { label: "Get a Custom Quote", intent: "lead_start" },
        { label: "View All Services", intent: "services" },
        { label: "View Portfolio", intent: "portfolio" }
      ],
      navigatePath: "/#pricing"
    };
  }

  // 3. Services Query Matcher
  if (isServices || text === "services") {
    const servicesText = BOT_KNOWLEDGE.services
      .map((s) => `• **${s.name}**: ${s.desc}`)
      .join("\n\n");

    return {
      text: `Here are the core services we provide at **Metatrybe**:\n\n${servicesText}`,
      buttons: [
        { label: "Pricing Details", intent: "pricing" },
        { label: "Start My Project", intent: "lead_start" },
        { label: "View Portfolio", intent: "portfolio" }
      ],
      navigatePath: "/services"
    };
  }

  // 4. Portfolio / Case Studies
  if (isPortfolio || text === "portfolio") {
    return {
      text: "You can explore our featured case studies, live designs, and interactive web projects directly on our **Work & Projects** section!",
      buttons: [
        { label: "Go to Projects Page", actionNavigate: "/projects" },
        { label: "View Pricing", intent: "pricing" },
        { label: "Request Custom Project", intent: "lead_start" }
      ],
      navigatePath: "/projects"
    };
  }

  // 5. Contact Information
  if (isContact || text === "contact") {
    return {
      text: `You can get in touch with us anytime!\n\n• **Email**: ${BOT_KNOWLEDGE.company.supportEmail}\n• **Response Time**: Within 24 hours\n• **Guarantee**: ${BOT_KNOWLEDGE.company.guarantee}`,
      buttons: [
        { label: "Book a Discovery Call / Quote", intent: "lead_start" },
        { label: "View Pricing", intent: "pricing" }
      ]
    };
  }

  // 6. Check FAQs
  for (const faq of BOT_KNOWLEDGE.faqs) {
    if (faq.keywords.some((kw) => text.includes(kw))) {
      return {
        text: faq.answer,
        buttons: [
          { label: "View Pricing Plans", intent: "pricing" },
          { label: "Book a Discovery Call", intent: "lead_start" }
        ]
      };
    }
  }

  // 7. Greeting
  if (isGreeting) {
    return {
      text: `Hello! Welcome to **Metatrybe**.\n\nI am your AI Website Assistant. How can I help you today? You can ask about our **services, pricing plans, portfolio**, or request a **custom project quote**!`,
      buttons: BOT_KNOWLEDGE.quickPrompts
    };
  }

  // 8. Fallback Default Response
  return {
    text: `I'd love to help you with that! At **Metatrybe**, we specialize in **Website Design & Dev**, **UX/UI Design**, **No-Code Apps**, **Custom Web/Mobile Apps**, and **E-Commerce Stores**.\n\nWould you like to check out our pricing tiers or request a quote for your project?`,
    buttons: [
      { label: "View Pricing", intent: "pricing" },
      { label: "View Services", intent: "services" },
      { label: "Get a Quote", intent: "lead_start" },
      { label: "Contact Info", intent: "contact" }
    ]
  };
}
